import type { WindowFlagName } from '../types/window';

/**
 * URL route ids for open desktop windows.
 * Multiple windows: `/about&projects&blogs`
 * Blog posts use their slug as a segment: `/blogs&my-post-slug`
 *
 * Note: desktop icon "ABOUT" maps to route `about` (Skills panel).
 * Desktop icon "CONTACT" maps to route `contact` (contact form panel).
 */
export type WindowRouteId =
  | 'about'
  | 'projects'
  | 'contact'
  | 'blogs'
  | 'credits'
  | 'terrain'
  | 'ships';

export const WINDOW_ROUTE_IDS: readonly WindowRouteId[] = [
  'about',
  'projects',
  'contact',
  'blogs',
  'credits',
  'terrain',
  'ships',
] as const;

export const ROUTE_TO_FLAG: Record<WindowRouteId, WindowFlagName> = {
  about: 'showHideSkills',
  projects: 'showHideProjects',
  contact: 'showHideAbout',
  blogs: 'showHideBlogs',
  credits: 'showHideCredits',
  terrain: 'showHideTerrain',
  ships: 'showHideShips',
};

export const FLAG_TO_ROUTE: Record<WindowFlagName, WindowRouteId> = {
  showHideSkills: 'about',
  showHideProjects: 'projects',
  showHideAbout: 'contact',
  showHideBlogs: 'blogs',
  showHideCredits: 'credits',
  showHideTerrain: 'terrain',
  showHideShips: 'ships',
};

/** A path segment: either a fixed panel or a blog post by slug. */
export type OpenSegment = { kind: 'panel'; id: WindowRouteId } | { kind: 'post'; slug: string };

export function isWindowRouteId(value: string): value is WindowRouteId {
  return (WINDOW_ROUTE_IDS as readonly string[]).includes(value);
}

/** Normalize a CMS slug for use as a path segment. */
export function normalizePostSlug(raw: string): string {
  return raw.trim().toLowerCase();
}

/**
 * Non-reserved path tokens are treated as blog post slugs.
 * (Reserved panel ids never open a post window.)
 */
export function isPostSlugSegment(value: string): boolean {
  const slug = normalizePostSlug(value);
  if (!slug || isWindowRouteId(slug)) {
    return false;
  }
  // Allow typical Hygraph-style slugs
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(slug);
}

function segmentKey(seg: OpenSegment): string {
  return seg.kind === 'panel' ? `panel:${seg.id}` : `post:${seg.slug}`;
}

function segmentsEqual(a: OpenSegment, b: OpenSegment): boolean {
  return segmentKey(a) === segmentKey(b);
}

/** Parse `/about&projects&my-slug` into an ordered focus stack (last = top). */
export function parseOpenSegments(pathname: string): OpenSegment[] {
  const raw = pathname.replace(/^\/+/, '').replace(/\/+$/, '');
  if (!raw) {
    return [];
  }

  const seen = new Set<string>();
  const ordered: OpenSegment[] = [];

  for (const part of raw.split('&')) {
    const id = part.trim().toLowerCase();
    if (!id) continue;

    let seg: OpenSegment | null = null;
    if (isWindowRouteId(id)) {
      seg = { kind: 'panel', id };
    } else if (isPostSlugSegment(id)) {
      seg = { kind: 'post', slug: normalizePostSlug(id) };
    }

    if (!seg) continue;
    const key = segmentKey(seg);
    if (seen.has(key)) continue;
    seen.add(key);
    ordered.push(seg);
  }

  return ordered;
}

/** Build path from open segments. Empty → `/`. */
export function buildWindowsPath(segments: OpenSegment[]): string {
  if (segments.length === 0) {
    return '/';
  }
  const parts = segments.map((s) => (s.kind === 'panel' ? s.id : s.slug));
  return `/${parts.join('&')}`;
}

export interface WindowVisibilityState {
  showHideSkills: boolean;
  showHideProjects: boolean;
  showHideAbout: boolean;
  showHideBlogs: boolean;
  showHideCredits: boolean;
  showHideTerrain: boolean;
  showHideShips: boolean;
  z_skills: number;
  z_projects: number;
  z_about: number;
  z_blogs: number;
  z_credits: number;
  z_terrain: number;
  z_ships: number;
  /** Blog post slugs open as their own windows (path order). */
  openBlogSlugs: string[];
}

/** Derive panel flags, z-order, and open blog posts from path. */
export function visibilityFromPath(pathname: string): WindowVisibilityState {
  const open = parseOpenSegments(pathname);

  const zForPanel = (id: WindowRouteId): number => {
    const index = open.findIndex((s) => s.kind === 'panel' && s.id === id);
    return index === -1 ? 0 : index + 1;
  };

  const openBlogSlugs = open
    .filter((s): s is { kind: 'post'; slug: string } => s.kind === 'post')
    .map((s) => s.slug);

  return {
    showHideSkills: open.some((s) => s.kind === 'panel' && s.id === 'about'),
    showHideProjects: open.some((s) => s.kind === 'panel' && s.id === 'projects'),
    showHideAbout: open.some((s) => s.kind === 'panel' && s.id === 'contact'),
    showHideBlogs: open.some((s) => s.kind === 'panel' && s.id === 'blogs'),
    showHideCredits: open.some((s) => s.kind === 'panel' && s.id === 'credits'),
    showHideTerrain: open.some((s) => s.kind === 'panel' && s.id === 'terrain'),
    showHideShips: open.some((s) => s.kind === 'panel' && s.id === 'ships'),
    z_skills: zForPanel('about'),
    z_projects: zForPanel('projects'),
    z_about: zForPanel('contact'),
    z_blogs: zForPanel('blogs'),
    z_credits: zForPanel('credits'),
    z_terrain: zForPanel('terrain'),
    z_ships: zForPanel('ships'),
    openBlogSlugs,
  };
}

/** z-index for a path segment (1-based index in the stack). */
export function zIndexForSegment(pathname: string, target: OpenSegment): number {
  const open = parseOpenSegments(pathname);
  const index = open.findIndex((s) => segmentsEqual(s, target));
  return index === -1 ? 0 : index + 1;
}

/** Open or focus a segment (moves it to end of the stack). */
export function openSegmentInList(current: OpenSegment[], segment: OpenSegment): OpenSegment[] {
  return [...current.filter((s) => !segmentsEqual(s, segment)), segment];
}

/** Close a segment if open. */
export function closeSegmentInList(current: OpenSegment[], segment: OpenSegment): OpenSegment[] {
  return current.filter((s) => !segmentsEqual(s, segment));
}

/** Toggle a panel segment. */
export function toggleSegmentInList(current: OpenSegment[], segment: OpenSegment): OpenSegment[] {
  if (current.some((s) => segmentsEqual(s, segment))) {
    return closeSegmentInList(current, segment);
  }
  return openSegmentInList(current, segment);
}

/** Panel helpers (compat with App open/close by WindowRouteId / flag). */
export function openRouteInList(current: OpenSegment[], route: WindowRouteId): OpenSegment[] {
  return openSegmentInList(current, { kind: 'panel', id: route });
}

export function closeRouteInList(current: OpenSegment[], route: WindowRouteId): OpenSegment[] {
  return closeSegmentInList(current, { kind: 'panel', id: route });
}

export function toggleRouteInList(current: OpenSegment[], route: WindowRouteId): OpenSegment[] {
  return toggleSegmentInList(current, { kind: 'panel', id: route });
}

export function openPostInList(current: OpenSegment[], slug: string): OpenSegment[] {
  return openSegmentInList(current, { kind: 'post', slug: normalizePostSlug(slug) });
}

export function closePostInList(current: OpenSegment[], slug: string): OpenSegment[] {
  return closeSegmentInList(current, { kind: 'post', slug: normalizePostSlug(slug) });
}
